import { useState, useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Card,
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  ToggleButton,
  shorthands,
} from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  CalendarTodayRegular,
} from '@fluentui/react-icons';
import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  format,
  isToday,
  isSameDay,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useReservations } from '../../hooks/useReservations';
import { useSlots } from '../../hooks/useSlots';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useDateLocale } from '../../hooks/useDateLocale';
import { BUSINESS_HOURS, type Reservation, type Slot } from '@cp-parkingslots/shared';
import TimeSlotCell from './TimeSlotCell';
import ReservationDialog from '../Reservations/ReservationDialog';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    maxWidth: '1400px',
    margin: '0 auto',
    '@media (max-width: 599px)': {
      gap: tokens.spacingVerticalS,
    },
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    '@media (max-width: 599px)': {
      gap: tokens.spacingVerticalS,
    },
  },
  weekInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 599px)': {
      gap: tokens.spacingHorizontalS,
    },
  },
  navButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  calendarCard: {
    overflow: 'auto',
    '@media (max-width: 599px)': {
      ...shorthands.borderRadius('0'),
      marginLeft: `-${tokens.spacingHorizontalL}`,
      marginRight: `-${tokens.spacingHorizontalL}`,
    },
  },
  // Desktop grid (7 days)
  gridDesktop: {
    display: 'grid',
    gridTemplateColumns: '60px repeat(7, 1fr)',
    gap: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  // Mobile grid (1 day with 2 slots side by side)
  gridMobile: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 1fr',
    gap: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  headerCell: {
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    textAlign: 'center',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
  },
  todayHeader: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  timeCell: {
    padding: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    textAlign: 'right',
    paddingRight: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  slotRow: {
    display: 'contents',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  slotLabel: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    marginBottom: tokens.spacingVerticalS,
    '@media (max-width: 599px)': {
      marginBottom: tokens.spacingVerticalXS,
    },
  },
  // Mobile day selector
  daySelector: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    marginBottom: tokens.spacingVerticalS,
  },
  dayButton: {
    minWidth: '44px',
    height: '44px',
  },
  currentDay: {
    textAlign: 'center',
    minWidth: '140px',
  },
  // Day pills for mobile week overview
  dayPills: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    overflowX: 'auto',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
    marginBottom: tokens.spacingVerticalS,
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  dayPill: {
    minWidth: '48px',
    flexShrink: 0,
  },
  cardContent: {
    padding: tokens.spacingHorizontalM,
    '@media (max-width: 599px)': {
      padding: tokens.spacingHorizontalS,
    },
  },
});

interface SelectedSlot {
  date: Date;
  hour: number;
  slotId: string;
}

export default function WeekView() {
  const styles = useStyles();
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const isMobile = useIsMobile();

  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);

  const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  const startDate = format(currentWeekStart, 'yyyy-MM-dd');
  const endDate = format(weekEnd, 'yyyy-MM-dd');

  const { data: reservations, isLoading, error } = useReservations(startDate, endDate);
  const { data: slots } = useSlots();

  const weekDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(currentWeekStart);
      day.setDate(currentWeekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [currentWeekStart]);

  const hours = useMemo(() => {
    const h: number[] = [];
    for (let i = BUSINESS_HOURS.startHour; i < BUSINESS_HOURS.endHour; i++) {
      h.push(i);
    }
    return h;
  }, []);

  const getReservationForSlot = (
    date: Date,
    hour: number,
    slotId: string
  ): Reservation | undefined => {
    if (!reservations) return undefined;
    const dateStr = format(date, 'yyyy-MM-dd');
    return reservations.find(
      (r) =>
        r.date === dateStr &&
        r.slotId === slotId &&
        hour >= r.startHour &&
        hour < r.endHour
    );
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentWeekStart(startOfWeek(today, { weekStartsOn: 1 }));
    setSelectedDay(today);
  };

  const goToPreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const goToPreviousDay = () => {
    const newDay = subDays(selectedDay, 1);
    setSelectedDay(newDay);
    // Update week if needed
    const newWeekStart = startOfWeek(newDay, { weekStartsOn: 1 });
    if (newWeekStart.getTime() !== currentWeekStart.getTime()) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const goToNextDay = () => {
    const newDay = addDays(selectedDay, 1);
    setSelectedDay(newDay);
    // Update week if needed
    const newWeekStart = startOfWeek(newDay, { weekStartsOn: 1 });
    if (newWeekStart.getTime() !== currentWeekStart.getTime()) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const selectDay = (day: Date) => {
    setSelectedDay(day);
  };

  const handleSlotClick = (date: Date, hour: number, slotId: string) => {
    const reservation = getReservationForSlot(date, hour, slotId);
    if (!reservation) {
      setSelectedSlot({ date, hour, slotId });
    }
  };

  if (error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>
          {t('calendar.error', { message: error.message })}
        </MessageBarBody>
      </MessageBar>
    );
  }

  // Mobile View - Single Day
  const renderMobileView = () => (
    <>
      {/* Day Navigation */}
      <div className={styles.daySelector}>
        <Button
          className={styles.dayButton}
          appearance="subtle"
          icon={<ChevronLeftRegular />}
          onClick={goToPreviousDay}
        />
        <div className={styles.currentDay}>
          <Text weight="semibold" size={400}>
            {format(selectedDay, 'EEE, d. MMM', { locale: dateLocale })}
          </Text>
        </div>
        <Button
          className={styles.dayButton}
          appearance="subtle"
          icon={<ChevronRightRegular />}
          onClick={goToNextDay}
        />
      </div>

      {/* Week Day Pills */}
      <div className={styles.dayPills}>
        {weekDays.map((day) => (
          <ToggleButton
            key={day.toISOString()}
            className={styles.dayPill}
            appearance={isSameDay(day, selectedDay) ? 'primary' : 'subtle'}
            checked={isSameDay(day, selectedDay)}
            size="small"
            onClick={() => selectDay(day)}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: tokens.fontSizeBase100 }}>
                {format(day, 'EEE', { locale: dateLocale })}
              </div>
              <div style={{ fontWeight: tokens.fontWeightSemibold }}>
                {format(day, 'd')}
              </div>
            </div>
          </ToggleButton>
        ))}
      </div>

      {/* Mobile Grid - Both slots side by side */}
      <Card className={styles.calendarCard}>
        <div className={styles.cardContent}>
          <div className={styles.gridMobile}>
            {/* Header row */}
            <div className={styles.headerCell}>{t('calendar.time')}</div>
            {slots?.map((slot) => (
              <div
                key={slot.id}
                className={`${styles.headerCell} ${
                  isToday(selectedDay) ? styles.todayHeader : ''
                }`}
              >
                {t(`slots.${slot.id}` as const).replace(t('slots.SLOT-A').split(' ')[0] + ' ', '')}
              </div>
            ))}

            {/* Time rows */}
            {hours.map((hour) => (
              <div key={hour} className={styles.slotRow}>
                <div className={styles.timeCell}>
                  {hour.toString().padStart(2, '0')}:00
                </div>
                {slots?.map((slot) => {
                  const reservation = getReservationForSlot(selectedDay, hour, slot.id);
                  return (
                    <TimeSlotCell
                      key={`${slot.id}-${hour}`}
                      date={selectedDay}
                      hour={hour}
                      slotId={slot.id}
                      reservation={reservation}
                      onClick={() => handleSlotClick(selectedDay, hour, slot.id)}
                      compact
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Card>
    </>
  );

  // Desktop View - Full Week
  const renderDesktopView = () => (
    <Card className={styles.calendarCard}>
      <div className={styles.cardContent}>
        {slots?.map((slot: Slot) => (
          <div key={slot.id} style={{ marginBottom: tokens.spacingVerticalM }}>
            <Text
              weight="semibold"
              size={300}
              className={styles.slotLabel}
              style={{ display: 'block' }}
            >
              {t(`slots.${slot.id}` as const)}
            </Text>
            <div className={styles.gridDesktop}>
              {/* Header row */}
              <div className={styles.headerCell}>{t('calendar.time')}</div>
              {weekDays.map((day) => (
                <div
                  key={day.toISOString()}
                  className={`${styles.headerCell} ${
                    isToday(day) ? styles.todayHeader : ''
                  }`}
                >
                  <div>{format(day, 'EEE', { locale: dateLocale })}</div>
                  <div style={{ fontSize: tokens.fontSizeBase200 }}>
                    {format(day, 'd.M.')}
                  </div>
                </div>
              ))}

              {/* Time rows */}
              {hours.map((hour) => (
                <div key={hour} className={styles.slotRow}>
                  <div className={styles.timeCell}>
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                  {weekDays.map((day) => {
                    const reservation = getReservationForSlot(day, hour, slot.id);
                    return (
                      <TimeSlotCell
                        key={`${day.toISOString()}-${hour}`}
                        date={day}
                        hour={hour}
                        slotId={slot.id}
                        reservation={reservation}
                        onClick={() => handleSlotClick(day, hour, slot.id)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );

  return (
    <div className={styles.container}>
      {/* Navigation Header */}
      <div className={styles.navigation}>
        <div className={styles.weekInfo}>
          <Text size={isMobile ? 400 : 500} weight="semibold">
            {format(currentWeekStart, 'MMMM yyyy', { locale: dateLocale })}
          </Text>
          <Text size={300} style={{ color: tokens.colorNeutralForeground2 }}>
            {t('calendar.week')} {format(currentWeekStart, 'w')}
          </Text>
        </div>
        <div className={styles.navButtons}>
          <Button
            appearance="subtle"
            icon={<CalendarTodayRegular />}
            onClick={goToToday}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : t('calendar.today')}
          </Button>
          {!isMobile && (
            <>
              <Button
                appearance="subtle"
                icon={<ChevronLeftRegular />}
                onClick={goToPreviousWeek}
              />
              <Button
                appearance="subtle"
                icon={<ChevronRightRegular />}
                onClick={goToNextWeek}
              />
            </>
          )}
        </div>
      </div>

      {/* Calendar Content */}
      {isLoading ? (
        <div className={styles.loading}>
          <Spinner label={t('calendar.loading')} />
        </div>
      ) : isMobile ? (
        renderMobileView()
      ) : (
        renderDesktopView()
      )}

      {/* Reservation Dialog */}
      {selectedSlot && (
        <ReservationDialog
          open={!!selectedSlot}
          onClose={() => setSelectedSlot(null)}
          date={selectedSlot.date}
          hour={selectedSlot.hour}
          slotId={selectedSlot.slotId}
        />
      )}
    </div>
  );
}
